import { Box, Button, Heading, Text } from '@everlywell/ui-kit';
import SectionContainer from 'components/PageSections/components/SectionContainer';
import { Link } from 'gatsby';
import { GatsbyImage, getSrc } from 'gatsby-plugin-image';
import React from 'react';
import { useInView } from 'react-intersection-observer';
import ewAnalytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import { replaceRedirectionLinkRoot } from 'utils/helpers/replaceRedirectionLinkRoot';
import { CallToAction, LongText, Media } from 'utils/types/contentfulTypes';

export type CtaBannerProps = {
  title?: string;
  description?: LongText;
  cta?: CallToAction;
  media?: Media;
  backgroundColor?: string;
  textColor?: string;
  primaryColor?: string;
  useBackgroundImage?: boolean;
};

/**
 * CtaBanner is a component that displays banner  with a title, description, and a call to action button.
 * It also accepts theme, where you can customize the color scheme.
 */
export default function CtaBanner({
  title,
  description,
  cta,
  media,
  backgroundColor,
  textColor,
  primaryColor,
  useBackgroundImage,
}: CtaBannerProps): JSX.Element {
  const [sectionRef, isSectionInView] = useInView({ triggerOnce: true });
  const { desktopImage, mobileImage, desktopImageAltText, mobileImageAltText } =
    media || {};

  const backgroundImage =
    useBackgroundImage && isSectionInView && desktopImage && mobileImage
      ? [`url(${getSrc(mobileImage)})`, null, `url(${getSrc(desktopImage)})`]
      : undefined;

  const isCtaFullUrl = cta?.url?.includes('http');

  const onCtaClick = () => {
    ewAnalytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        sectionHeader: title ?? '',
        contentfulSection: 'Cta Banner',
        ctaText: cta?.label ?? '',
      },
    });
  };

  return (
    <SectionContainer
      ref={sectionRef}
      size={['fluid', 'fluid', 'fluid', 'fluid', 'ultra']}
      justifyContent="space-between"
      backgroundColor={backgroundColor ?? 'transparent'}
      flexDirection={['column', 'column', 'row']}
      marginY={[0, 0, useBackgroundImage ? 0 : 6]}
      centerContent
      // this is the height of the banner when using a background image as specified in the design
      height={useBackgroundImage ? ['711px', null, '622px'] : 'auto'}
      backgroundImage={backgroundImage}
      backgroundSize="cover"
      backgroundPosition={['100% 0%', '100% 60%', '100% 0%']}
      backgroundRepeat="no-repeat"
    >
      <Box
        display="flex"
        flexDirection={['column', 'column', 'row']}
        width={['100%', '100%', '45%']}
      >
        <Box
          marginTop={[12]}
          padding={[6, 6, 12, 16]}
          flex={['unset', 'unset', 1]}
          display="flex"
          flexDirection="column"
          alignItems={['center', 'center', 'flex-start']}
          justifyContent="center"
        >
          <Box
            marginTop={[6, 6, 0]}
            marginBottom={[6, 6, 0]}
            display="flex"
            flexDirection={['column']}
            alignItems={['center', 'center', 'flex-start']}
          >
            {title ? (
              <Heading
                // @ts-ignore
                size={['32px', '2xl']}
                as="h2"
                marginBottom={3}
                textAlign={['center', 'center', 'left']}
                color={textColor ? textColor : 'tints.black'}
              >
                {title}
              </Heading>
            ) : null}

            {description?.description ? (
              <Text
                size="xl"
                marginBottom={[6, 10]}
                textAlign={['center', 'left']}
                color={textColor ? textColor : 'tints.black'}
              >
                {description.description}
              </Text>
            ) : null}

            {cta?.label && cta?.url && (
              <>
                {isCtaFullUrl ? (
                  <Button
                    variant="primary"
                    as="a"
                    href={replaceRedirectionLinkRoot(cta.url)}
                    onClick={() => onCtaClick()}
                  >
                    {cta.label}
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    as={Link}
                    to={cta.url}
                    onClick={onCtaClick}
                  >
                    {cta.label}
                  </Button>
                )}
              </>
            )}
          </Box>
        </Box>

        {mobileImage && !useBackgroundImage ? (
          <Box
            display={['flex', 'flex', 'none']}
            width="100%"
            height="450px"
            flex={['unset', 'unset', 1]}
            justifyContent="end"
          >
            <GatsbyImage
              image={mobileImage.gatsbyImageData}
              alt={mobileImageAltText || ''}
              imgStyle={{ objectPosition: 'top' }}
            />
          </Box>
        ) : null}
      </Box>
      {desktopImage && !useBackgroundImage ? (
        <Box
          display={['none', 'none', 'block']}
          flex={['unset', 'unset', 1]}
          maxHeight="650"
          maxWidth="450"
          overflow="hidden"
        >
          <GatsbyImage
            image={desktopImage.gatsbyImageData}
            alt={desktopImageAltText || ''}
            style={{ height: '100%' }}
            imgStyle={{ objectPosition: 'top' }}
          />
        </Box>
      ) : null}
    </SectionContainer>
  );
}
